import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Outlet } from "react-router-dom";
import { useAppToast } from "@/hooks/useAppToast";
import {
  getPackage,
  updatePackage,
  deletePackage,
  createPromotion,
  getPromotions,
  deletePromotion,
  updatePromotion,
} from "@/services/admin/packages.service";
import {
  Package,
  UpdatePackageInputs,
  CreatePromotionInput,
  UpdatePromotionInput,
  Promotion,
} from "@/services/admin/packages.interfaces";
import { AdminPageLayout } from "../AdminPageLayout";
import { Button } from "@/components/ui/button";
import { Trash } from "lucide-react";

// Importar componentes
import BackButtons from "./BackButtons";
import PackageInfoForm from "./PackageInfoForm";
import PromotionsTable from "./PromotionsTable";
import ModuleList from "./ModuleList";

const AdminEditPackagePage: React.FC = () => {
  const { packageId } = useParams<{ packageId: string }>();
  const navigate = useNavigate();
  const toast = useAppToast();

  const [isLoading, setIsLoading] = useState(true);
  const [packageData, setPackageData] = useState<Package | null>(null);
  const [promotions, setPromotions] = useState<Promotion[]>([]);


  const handleDeletePromotion = async (promotionId: string) => {
    if (!packageId) return;
  
    try {
      await toast.promise(
        deletePromotion(packageId, promotionId),
        {
          loading: "Excluindo promoção...",
          success: "Promoção excluída com sucesso",
          error: "Falha ao excluir promoção"
        }
      );
      
      if (packageId) {
        setPromotions(promotions.filter(p => p.id !== promotionId));
      }
    } catch (error) {
      console.error("Erro ao excluir promoção:", error);
    }
  };

  const handleCreatePromotion = async (data: CreatePromotionInput) => {
    if (!packageId) return;
    
    try {
      await createPromotion(packageId, data);
      // Recarregar promoções para obter a nova promoção
      const updatedPromotions = await getPromotions(packageId);
      setPromotions(updatedPromotions);
    } catch (error) {
      console.error("Erro ao criar promoção:", error);
      throw error;
    }
  };

  const handleUpdatePromotion = async (
    promotionId: string,
    data: UpdatePromotionInput
  ) => {
    if (!packageId) return;
    
    try {
      await updatePromotion(packageId, promotionId, data);
      // Recarregar promoções para obter os dados atualizados
      const updatedPromotions = await getPromotions(packageId);
      setPromotions(updatedPromotions);
    } catch (error) {
      console.error("Erro ao atualizar promoção:", error);
      throw error;
    }
  };

  useEffect(() => {
    const loadData = async () => {
      if (!packageId) {
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      
      try {
        const data = await getPackage(packageId);
        setPackageData(data);

        // Carregar promoções
        try {
          const promotionsData = await getPromotions(packageId);
          setPromotions(promotionsData);
        } catch (error) {
          console.error("Erro ao carregar promoções:", error);
          toast.error("Falha ao carregar promoções");
        }
      } catch (error) {
        console.error("Erro ao carregar pacote:", error);
        toast.error(error instanceof Error ? error.message : "Falha ao carregar dados do pacote");
        navigate("/admin/packages"); // Redirecionar em caso de erro
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [packageId]);

  const handleSave = async (changes: UpdatePackageInputs) => {
    if (!packageId) return;

    try {
      await updatePackage(packageId, changes);
      setPackageData((prev) => (prev ? { ...prev, ...changes } : null));
    } catch (error) {
      console.error("Erro ao atualizar pacote:", error);
      throw error;
    }
  };

  const handleDelete = async () => {
    if (!packageId) return;

    const confirmed = window.confirm(
      "Tem certeza que deseja excluir este pacote?"
    );
    if (!confirmed) return;

    try {
      await toast.promise(deletePackage([packageId]), {
        loading: "Excluindo pacote...",
        success: "Pacote excluído com sucesso",
        error: "Falha ao excluir pacote",
      });
      navigate("/admin/packages");
    } catch (error) {
      console.error("Erro ao excluir pacote:", error);
    }
  };

  if (isLoading) {
    return (
      <AdminPageLayout title="Carregando Pacote...">
        <div className="flex flex-col items-center justify-center py-16">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mb-4"></div>
          <p className="text-neutral-600">Carregando dados do pacote...</p>
        </div>
      </AdminPageLayout>
    );
  }

  if (!packageData) {
    return (
      <AdminPageLayout title="Pacote Não Encontrado">
        <div className="flex items-center justify-center py-8">
          Pacote não encontrado
        </div>
      </AdminPageLayout>
    );
  }

  return (
    <AdminPageLayout title={`Editar ${packageData.name}`}>
      <main className="mx-auto py-8">
        <BackButtons />
        <PackageInfoForm packageData={packageData} onSave={handleSave} />
        <PromotionsTable
          promotions={promotions}
          onDelete={handleDeletePromotion}
          onAdd={handleCreatePromotion}
          onEdit={handleUpdatePromotion}
        />
        {packageId && <ModuleList packageId={packageId} />}
        <footer className="flex justify-end items-center border-t pt-6">
          <Button
            variant="ghost"
            className="text-neutral-600"
            onClick={handleDelete}
          >
            <Trash className="h-4 w-4 mr-2" /> Excluir Pacote
          </Button>
        </footer>
        <Outlet />
      </main>
    </AdminPageLayout>
  );
};

export default AdminEditPackagePage;
